import { Alert, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../../../api/gql';
import { AresLookupButton, AresLookupResult } from '../../../../components/AresLookupButton';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { useCustomerLookup } from '../../../../hooks/useCustomerLookup';
import { CustomerFormPart } from '../form-parts/CustomerFormPart';
import { inputSharedProps } from '../shared';
import { InvoiceFormType } from '../types';

type Props = {
  readonly?: boolean;
};

export const ConnectedCustomerForm = ({ readonly }: Props) => {
  const { control, getFieldState, getValues, formState, resetField, setValue } = useFormContext<InvoiceFormType>();
  const { t } = useTranslation();

  const [isLookedUp, setIsLookedUp] = useState(false);

  const vatIdFieldState = getFieldState('customer.vatId', formState);
  const emailState = getFieldState('customer.email', formState);
  const phoneNumberState = getFieldState('customer.phoneNumber', formState);
  const isRemindingToneDirty = getFieldState('customer.remindingTone', formState).isDirty;

  const { isLoading, data } = useCustomerLookup();

  const prefillCustomerFromContact = useCallback(
    (contact: Partial<Customer>) => {
      setValue('customer.taxId', contact.taxId ?? '', { shouldValidate: true });
      setValue('customer.name', contact.name ?? '', { shouldValidate: true });
      setValue('customer.address', contact.address ?? '', {
        shouldValidate: true,
      });
      resetField('customer.email', { defaultValue: contact.email ?? '' });
      resetField('customer.phoneNumber', {
        defaultValue: contact.phoneNumber ?? '',
      });
    },
    [resetField, setValue],
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    const vatId = getValues('customer.vatId');
    if (!vatId) {
      return;
    }
    const lookup = data.customers.find((customer) => customer.vatId === vatId);
    if (lookup && lookup.name) {
      prefillCustomerFromContact(lookup);
      setIsLookedUp(true);
    }
  }, [data, getValues, prefillCustomerFromContact]);

  const isContact = useMemo(
    () => isLookedUp && !vatIdFieldState.invalid && !vatIdFieldState.isDirty,
    [isLookedUp, vatIdFieldState.invalid, vatIdFieldState.isDirty],
  );

  const isContactInfoDirty = useMemo(
    () => isContact && (emailState.isDirty || phoneNumberState.isDirty),
    [isContact, emailState.isDirty, phoneNumberState.isDirty],
  );

  const inputProps = {
    ...inputSharedProps,
  };

  const handleAresLookupSuccess = useCallback(
    (result: AresLookupResult) => {
      if (result.taxId) {
        setValue('customer.taxId', result.taxId, { shouldValidate: true });
      }
      if (result.name) {
        setValue('customer.name', result.name, { shouldValidate: true });
      }
      if (result.address) {
        setValue('customer.address', result.address, { shouldValidate: true });
      }
    },
    [setValue],
  );

  return (
    <Stack sx={{ position: 'relative' }}>
      {isLoading ? <FullSizeLoader /> : null}
      {isContact ? (
        <Alert severity={'info'} sx={{ mt: 2 }}>
          {t('contactPrefilled')}
        </Alert>
      ) : null}
      <CustomerFormPart
        readonly={readonly}
        inputProps={inputProps}
        control={control}
        slots={{
          actionArea: (
            <AresLookupButton
              getVatId={() => getValues('customer.vatId') ?? ''}
              onLookupSuccess={handleAresLookupSuccess}
            />
          ),
          afterContact: isContactInfoDirty ? (
            <Stack gap={1}>
              {isContactInfoDirty ? (
                <Alert severity={'warning'} sx={{ whiteSpace: 'break-spaces' }}>
                  {t('contactChangeWarning')}
                </Alert>
              ) : null}
            </Stack>
          ) : null,
          end: isRemindingToneDirty ? (
            <Alert severity={'warning'}>{t('remindingTone.change.warningForFutureInvoices')}</Alert>
          ) : null,
        }}
      />
    </Stack>
  );
};
